/**
 * Страница работы в загрузке (agroai-6al.49): блоки в тех же рамках, с теми же
 * заголовками и высотами, что у готовой страницы, — данные встают на место без
 * сдвига. Все блоки `quiet`, когда стоят внутри скелетона страницы.
 */
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { levelSpec } from '../../lib/levels';
import { ScopeBarSkeleton } from '../../astra/ScopeBar';
import { Stack } from '../../ui/Card';
import { Skeleton, SkeletonCard, SkeletonChart, SkeletonList, SkeletonMap, SkeletonPage, SkeletonTable, SkeletonText } from '../../ui/Skeleton';

const m = defineMessages({
  progressLabel: { id: 'work.skeleton.progress.label', defaultMessage: 'ход работы', description: 'Подпись для программ экрана (aria-label) пока загружается карточка «Ход работы»; винительный падеж' },
  progressTitle: { id: 'work.skeleton.progress.title', defaultMessage: 'Ход работы', description: 'Заголовок карточки «Ход работы» на экране загрузки страницы работы' },
  waterLabel: { id: 'work.skeleton.water.label', defaultMessage: 'воду полива', description: 'Подпись для программ экрана (aria-label) пока загружается карточка «Вода полива»; винительный падеж' },
  waterTitle: { id: 'work.skeleton.water.title', defaultMessage: 'Вода полива', description: 'Заголовок карточки «Вода полива» на экране загрузки страницы работы' },
  farmsLabel: { id: 'work.skeleton.farms.label', defaultMessage: 'хозяйства работы', description: 'Подпись для программ экрана (aria-label) пока загружается таблица хозяйств работы; винительный падеж' },
  farmsTitle: { id: 'work.skeleton.farms.title', defaultMessage: 'Хозяйства по статусу', description: 'Заголовок таблицы «Хозяйства по статусу» на экране загрузки страницы работы' },
  paceLabel: { id: 'work.skeleton.pace.label', defaultMessage: 'дневной ряд', description: 'Подпись для программ экрана (aria-label) пока загружается график хода работы по дням; винительный падеж' },
  paceTitle: { id: 'work.skeleton.pace.title', defaultMessage: 'Ход работы по дням', description: 'Заголовок графика «Ход работы по дням» на экране загрузки страницы работы' },
  mapLabel: { id: 'work.skeleton.map.label', defaultMessage: 'карту работы', description: 'Подпись для программ экрана (aria-label) пока загружается карта работы; винительный падеж' },
  mapTitle: { id: 'work.skeleton.map.title', defaultMessage: 'Карта работы: контуры по статусу', description: 'Заголовок карточки с картой работы на экране загрузки страницы работы' },
  page: { id: 'work.skeleton.page', defaultMessage: 'страницу работы', description: 'Подпись для программ экрана (aria-label) пока загружается вся страница работы; винительный падеж' },
});

/** «Ход работы»: трёхисходная полоса слева, три факта справа. */
export function WorkProgressSkeleton({ quiet }: { quiet?: boolean }) {
  const t = useT();
  return (
    <SkeletonCard quiet={quiet} size="lg" label={t(m.progressLabel)} title={t(m.progressTitle)} height={150}>
      <div className="wcamp__progress" aria-hidden="true">
        <span className="skel-text">
          <Skeleton variant="block" width="100%" height={44} />
          <Skeleton width="60%" className="skel--caption" />
        </span>
        <span className="skel-text">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} width="100%" />)}
        </span>
      </div>
    </SkeletonCard>
  );
}

/** «Вода полива». */
export function WorkWaterSkeleton({ quiet }: { quiet?: boolean }) {
  const t = useT();
  return (
    <SkeletonCard quiet={quiet} size="lg" label={t(m.waterLabel)} title={t(m.waterTitle)} height={220}>
      <SkeletonText lines={5} />
    </SkeletonCard>
  );
}

/** «Хозяйства по статусу» (таблица). */
export function WorkFarmsSkeleton({ quiet }: { quiet?: boolean }) {
  const t = useT();
  return <SkeletonTable quiet={quiet} label={t(m.farmsLabel)} title={t(m.farmsTitle)} rows={6} cols={5} />;
}

/** «Ход работы по дням». */
export function WorkPaceSkeleton({ quiet, heading }: { quiet?: boolean; heading?: string }) {
  const t = useT();
  return <SkeletonChart quiet={quiet} label={t(m.paceLabel)} title={heading ?? t(m.paceTitle)} height={220} bars={14} captionLines={1} />;
}

/** Карта работы со списком статусов справа. */
export function WorkMapSkeleton({ quiet }: { quiet?: boolean }) {
  const t = useT();
  return (
    <SkeletonCard quiet={quiet} size="lg" label={t(m.mapLabel)} title={t(m.mapTitle)}>
      <div className="wcamp__mapgrid">
        <SkeletonMap quiet height={460} />
        <SkeletonList quiet items={5} tone="soft" />
      </div>
    </SkeletonCard>
  );
}

/** Всё тело кампании на каноне: прогресс, карта, хозяйства, дневной ряд. */
export function WorkCampaignSkeleton({ quiet, water }: { quiet?: boolean; water?: boolean }) {
  return (
    <>
      <WorkProgressSkeleton quiet={quiet} />
      {water ? <WorkWaterSkeleton quiet={quiet} /> : null}
      <WorkMapSkeleton quiet={quiet} />
      <div className="wcamp__lower">
        <WorkFarmsSkeleton quiet={quiet} />
        <WorkPaceSkeleton quiet={quiet} />
      </div>
    </>
  );
}

/** Страница работы целиком, пока не пришёл срез (`Loading`). */
export function WorkPageSkeleton() {
  const t = useT();
  return (
    <SkeletonPage label={t(m.page)}>
      <Stack>
        {/* Шапка работы: название, две строки описания и строка этапа/окна. */}
        <header className="pagehead" aria-hidden="true">
          <div className="pagehead__main">
            <div className="pagehead__level">{levelSpec('work').caption}</div>
            <Skeleton variant="block" width="min(560px, 100%)" height={40} />
            <div className="pagehead__purpose" style={{ display: 'grid', gap: 'var(--sp-2)' }}>
              <Skeleton width="min(660px, 100%)" />
              <Skeleton width="min(140px, 100%)" />
            </div>
            <Skeleton width="min(600px, 100%)" />
          </div>
        </header>
        <ScopeBarSkeleton />
        <WorkCampaignSkeleton quiet />
      </Stack>
    </SkeletonPage>
  );
}
